import { mailIdentity } from './mailDashboardApi';
import { useMailRuntime } from "./context/MailRuntime";
import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  Type, Image, Columns, Layout, Minus, Space,
  LayoutTemplate, PanelTop, MousePointerClick,
  ImagePlus, BookOpen, Share2, Video,
  ArrowRight, ArrowLeft, ChevronLeft, PanelLeftClose, PanelRightClose,
} from "lucide-react";
import { ComposerProvider, useComposerContext } from "./context/ComposerContext";
import { BlockStylesProvider, useBlockStyles } from "./context/BlockStylesContext";
import { PRESETS } from "./config/presets";
import {
  makeTextBlock,
  makeHeaderBlock,
  makeFooterBlock,
  makeBannerBlock,
  makeSectionBlock,
  makeTwoColBlock,
  makeImageTextBlock,
  makeDividerBlock,
  makeSpacerBlock,
  makeButtonBlock,
  makeImageGalleryBlock,
  makeBannerImageBlock,
} from "./blocks/factories";
import { makeBookCardBlock } from "./blocks/bookCardFactory";
import { makeSocialIconsBlock } from "./blocks/socialIconsFactory";
import { makeVideoEmbedBlock } from "./blocks/videoEmbedFactory";

import useEditorDom from "./hooks/useEditorDom";
import useDraftPersistence from "./hooks/useDraftPersistence";
import useImageUpload from "./hooks/useImageUpload";
import useTemplates from "./hooks/useTemplates";
import useNewsletterApi from "./hooks/useNewsletterApi";
import useAutoSave from "./hooks/useAutoSave";
import useUndoRedo from "./hooks/useUndoRedo";

import BlocksSidebar from "./panels/BlocksSidebar";
import EditorCanvas from "./panels/EditorCanvas";
import DesignSidebar from "./panels/DesignSidebar";
import BrandingBanner from "./panels/BrandingBanner";
import ReviewScreen from "./panels/ReviewScreen";
// SectionMenu removed — replaced by InlineBlockActions
import ImagePopover from "./panels/ImagePopover";
import ButtonSettingsPopover from "./panels/ButtonSettingsPopover";
import PreviewToggle from "./ui/PreviewToggle";
import SaveIndicator from "./ui/SaveIndicator";
import SocialLinksModal from "./modals/SocialLinksModal";
import NewConfirmModal from "./modals/NewConfirmModal";
import InputModal from "./modals/InputModal";
import AutoSaveToast from "./ui/AutoSaveToast";
import InlineBlockActions from "./ui/InlineBlockActions";
import BlockSettingsPanel from "./panels/BlockSettingsPanel";
import buildEmailHtml from "./email/buildEmailHtml";

function ComposerInner() {
  const runtime = useMailRuntime();
  const ENABLE_INLINE_SOCIAL_SNIPPET_INSERTION = false;
  const headerOffset = "56px";
  const ctx = useComposerContext();
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [socialModalPlacement, setSocialModalPlacement] = useState("body");
  const [newConfirmOpen, setNewConfirmOpen] = useState(false);
  const [headerLinkModalOpen, setHeaderLinkModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const isDirtyRef = useRef(false);

  const [blockSettingsPos, setBlockSettingsPos] = useState(null);
  const blockSettingsRef = useRef(null);

  const blockStylesCtx = useBlockStyles();
  const editorDom = useEditorDom();
  const draftPersistence = useDraftPersistence(editorDom);
  const imageUpload = useImageUpload(editorDom);
  const templates = useTemplates(draftPersistence);
  const newsletterApi = useNewsletterApi(editorDom, draftPersistence);
  const { autoSaveStatus, triggerSave } = useAutoSave(draftPersistence.saveDraftLocal);
  const undoRedo = useUndoRedo(ctx.editorRef, () => {
    const { newsletterId, version, ...document } = draftPersistence.capture(); return document;
  }, value => draftPersistence.restoreDocument({ ...value, newsletterId: ctx.newsletterId, version: ctx.version }));
  const live = useRef({}); live.current = { ctx, draftPersistence, undoRedo, triggerSave };
  const metadata = JSON.stringify({ templateVersion: ctx.templateVersion, subject: ctx.subject, recipients: ctx.recipients, selectedList: ctx.selectedList, preheaderText: ctx.preheaderText, design: ctx.getDesignFallback(), footer: ctx.footer, blockStyles: blockStylesCtx.blockStyles, newsletterId: ctx.newsletterId, version: ctx.version });
  useEffect(() => {
    if (!ctx.ready) return;
    isDirtyRef.current = true; triggerSave(); live.current.undoRedo.snapshot();
  }, [metadata, ctx.ready, triggerSave]);


  useEffect(() => {
    const editor = ctx.editorRef.current;
    if (!editor) return;
    const observer = new MutationObserver(() => {
      if (!live.current.ctx.ready) return;
      isDirtyRef.current = true;
      triggerSave(); live.current.undoRedo.snapshot();
    });
    observer.observe(editor, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["style", "src", "alt", "href", "data-link", "data-book-layout", "data-book-border"] });
    return () => observer.disconnect();
  }, [ctx.editorRef, triggerSave]);

  /* ---- INIT ---- */
  useEffect(() => {
    templates.loadTemplatesFromApi();


    const params = new URLSearchParams(window.location.search);
    const id = params.get("newsletterId");

    if (id) {
      ctx.setNewsletterId(id);
      newsletterApi.loadNewsletterFromApi(id);
    } else {
      try { const restored = draftPersistence.restoreDraft(); if (!restored && runtime.fixedTemplate) templates.loadInitialTemplate(); } catch (error) { ctx.setLoadError('Unable to restore the browser draft: ' + error.message); }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- CLICK HANDLERS (outside dismiss + block selection) ---- */
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        ctx.sectionMenuRef.current &&
        !ctx.sectionMenuRef.current.contains(e.target) &&
        !e.target.closest("[data-bc-settings]")
      ) {
        ctx.setActiveSection(null);
      }

      if (
        ctx.imagePopoverRef.current &&
        !ctx.imagePopoverRef.current.contains(e.target) &&
        !e.target.closest("img[data-thumb]") &&
        !e.target.closest("[data-image-slot]")
      ) {
        ctx.setImagePopover((prev) => ({ ...prev, open: false }));
      }

      if (
        ctx.buttonPopoverRef.current &&
        !ctx.buttonPopoverRef.current.contains(e.target) &&
        !e.target.closest("[data-bc-button]")
      ) {
        ctx.setButtonPopover((prev) => ({ ...prev, open: false }));
      }

      // Detect block click for per-block styling
      // Don't deselect if clicking inside the right sidebar, inline actions bar, or modals
      const inSidebar = e.target.closest("aside");
      const inInlineActions = e.target.closest("[data-bc-inline-actions]");
      const inPopover = e.target.closest("[data-bc-popover]");
      const inBlockSettings = blockSettingsRef.current && blockSettingsRef.current.contains(e.target);
      const inModal = e.target.closest("[class*='fixed inset-0']");
      const blockEl = e.target.closest("[data-bc-id]");
      if (!inBlockSettings && blockStylesCtx.activeAdvancedBlockId && !e.target.closest("[data-bc-open-advanced]")) {
        const advancedEl = document.querySelector(`[data-bc-id="${blockStylesCtx.activeAdvancedBlockId}"]`);
        if (!advancedEl || !advancedEl.contains(e.target)) {
          blockStylesCtx.closeAdvancedBlock();
        }
      }

      if (blockEl) {
        const nextBlockId = blockEl.dataset.bcId;
        if (blockStylesCtx.activeBlockId !== nextBlockId) {
          blockStylesCtx.closeAdvancedBlock();
        }
        blockStylesCtx.setActiveBlockId(nextBlockId);
      } else if (!inSidebar && !inInlineActions && !inPopover && !inModal && ctx.editorRef.current && !ctx.editorRef.current.contains(e.target)) {
        blockStylesCtx.setActiveBlockId(null);
        blockStylesCtx.closeAdvancedBlock();
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const blockId = blockStylesCtx.activeAdvancedBlockId;
    if (!blockId) {
      setBlockSettingsPos(null);
      return;
    }

    const updatePos = () => {
      const el = document.querySelector(`[data-bc-id="${blockId}"]`);
      if (!el) {
        setBlockSettingsPos(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      const panelWidth = 300;
      let top = rect.top + window.scrollY + 12;
      const minTop = window.scrollY + 72;
      top = Math.max(minTop, top);
      const left = Math.min(window.scrollX + window.innerWidth - panelWidth - 12, rect.right + window.scrollX + 12);
      setBlockSettingsPos({ top, left });
    };

    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [blockStylesCtx.activeAdvancedBlockId]);

  /* ---- HEADER SYNC EFFECTS ---- */
  useEffect(() => {
    const header = ctx.editorRef.current?.querySelector("[data-header-bg]");
    if (!header) return;
    header.style.background = ctx.headerBg;
    header.setAttribute("data-header-bg", ctx.headerBg);
  }, [ctx.headerBg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const header = ctx.editorRef.current?.querySelector("[data-header-bg]");
    if (!header) return;
    header.style.color = ctx.headerTextColor;
    header.querySelectorAll("a, [contenteditable='true']").forEach((el) => {
      el.style.color = ctx.headerTextColor;
    });
  }, [ctx.headerTextColor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const header = ctx.editorRef.current?.querySelector("[data-header-bg]");
    if (!header) return;
    header.querySelectorAll("a[data-style='button']").forEach((btn) => {
      btn.style.background = ctx.headerButtonColor;
    });
  }, [ctx.headerButtonColor, ctx.headerBg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const header = ctx.editorRef.current?.querySelector("[data-header-bg]");
    if (!header) return;
    header.querySelectorAll("a[data-style='button']").forEach((btn) => {
      btn.style.color = ctx.headerButtonText;
    });
  }, [ctx.headerButtonText]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- EVENT DELEGATION: image/slot/button clicks inside the editor ---- */
  useEffect(() => {
    const editor = ctx.editorRef.current;
    if (!editor) return;

    const handleEditorClick = (e) => {
      // Image click (data-thumb or data-banner-img)
      const imgEl = e.target.closest("img[data-thumb], img[data-banner-img]");
      if (imgEl) {
        e.preventDefault();
        e.stopPropagation();
        window.__bcHandleImageClick && window.__bcHandleImageClick(imgEl);
        return;
      }

      // Placeholder slot click
      const slotEl = e.target.closest("[data-image-slot]");
      if (slotEl) {
        e.preventDefault();
        e.stopPropagation();
        window.__bcImageSlotClick && window.__bcImageSlotClick(slotEl);
        return;
      }



      // Button click (data-bc-button)
      const btnEl = e.target.closest("[data-bc-button]");
      if (btnEl) {
        e.preventDefault();
        e.stopPropagation();
        window.__bcButtonClick && window.__bcButtonClick(btnEl);
        return;
      }
    };

    editor.addEventListener("click", handleEditorClick);
    return () => editor.removeEventListener("click", handleEditorClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- GLOBAL CALLBACKS ---- */
  useEffect(() => {
    const cleanupImages = imageUpload.ensureGlobalImageHandlers();
    window.__bcOpenImageActions = imageUpload.openImageActions;
    window.__bcOpenSlotActions = imageUpload.openSlotActions;

    window.__bcOpenAdvanced = (advancedEl) => {
      const blockEl = advancedEl?.closest("[data-bc-id]");
      const blockId = blockEl?.dataset?.bcId || advancedEl?.dataset?.bcTarget;
      if (!blockId) return;
      blockStylesCtx.setActiveBlockId(blockId);
      blockStylesCtx.openAdvancedBlock(blockId);
    };

    // Button click handler — opens ButtonSettingsPopover
    window.__bcButtonClick = (btnEl) => {
      if (!btnEl) return;
      window.__bcActiveButton = btnEl;
      const rect = btnEl.getBoundingClientRect();
      const popH = 220;
      const popW = 260;
      const viewH = window.innerHeight;
      const pad = 8;

      // Prefer below the button, but if too low, put it above
      let top = rect.bottom + pad;
      if (rect.bottom + popH + pad > viewH) {
        top = rect.top - popH - pad;
      }
      // Never above the sticky header
      top = Math.max(90, top);

      let left = rect.left + rect.width / 2 - popW / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - popW - 8));

      ctx.setButtonPopover({
        open: true,
        top,
        left,
        text: btnEl.getAttribute("data-btn-label") || btnEl.textContent.trim(),
        url: btnEl.getAttribute("data-btn-url") || btnEl.href || "",
      });
    };
    return () => { cleanupImages?.(); for (const key of ['__bcOpenImageActions','__bcOpenSlotActions','__bcOpenAdvanced','__bcButtonClick','__bcActiveButton','__bcAddImageToRow']) delete window[key]; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- BEFOREUNLOAD WARNING ---- */
  useEffect(() => {
    const flush = () => { if (live.current.ctx.ready) { try { live.current.draftPersistence.saveDraftLocal(); } catch { return false; } } return true; };
    const unload = event => { flush(); if (isDirtyRef.current || live.current.ctx.operationLock.current) { event.preventDefault(); event.returnValue = ''; } };
    const leave = event => { if (live.current.ctx.operationLock.current || (isDirtyRef.current && !window.confirm('Leave the composer? Browser recovery is local to this device; use Save to keep a copy in your account.'))) { event.preventDefault(); return; } if (!flush()) { event.preventDefault(); live.current.ctx.setNotice('Browser recovery could not be saved. Save to your account before leaving.'); } };
    window.addEventListener('beforeunload', unload); window.addEventListener('bookish:before-navigation', leave);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener('bookish:before-navigation', leave); };
  }, []);

  /* ---- BLOCK INSERTERS ---- */
  const handleAddHeaderLink = useCallback(() => {
    const root = editorDom.getRoot();
    if (!root) return;

    const nav = root.querySelector("[data-header-nav]");
    if (!nav) {
      alert("Add a header first.");
      return;
    }

    const existingLinks = nav.querySelectorAll("a");
    if (existingLinks.length >= 3) {
      alert("Maximum 3 header links.");
      return;
    }

    setHeaderLinkModalOpen(true);
  }, [editorDom]);

  const handleHeaderLinkSave = useCallback(({ label, url }) => {
    const root = editorDom.getRoot();
    if (!root) return;
    const nav = root.querySelector("[data-header-nav]");
    if (!nav) return;

    const link = document.createElement("a");
    link.href = url;
    link.textContent = label;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.dataset.style = "link";
    link.style.color = "#ffffff";
    link.style.textDecoration = "underline";
    link.style.cursor = "pointer";
    link.style.padding = "4px 6px";

    link.onclick = (e) => {
      e.preventDefault();
      if (link.dataset.style === "link") {
        link.dataset.style = "button";
        link.style.textDecoration = "none";
        link.style.background = "#ffffff";
        link.style.color = "#000000";
        link.style.padding = "6px 12px";
        link.style.borderRadius = "6px";
      } else {
        link.dataset.style = "link";
        link.style.background = "none";
        link.style.color = "#ffffff";
        link.style.textDecoration = "underline";
        link.style.padding = "4px 6px";
        link.style.borderRadius = "0";
      }
    };

    link.ondblclick = () => {
      const ok = window.confirm("Remove this header link?");
      if (ok) link.remove();
    };

    nav.appendChild(link);
  }, [editorDom]);

  const insertHeaderBlock = useCallback(() => {
    editorDom.ensureRoot();
    const root = editorDom.getRoot();
    if (!root) return;
    if (root.querySelector('[data-bc-section="header"]')) {
      alert("Only one header allowed.");
      return;
    }
    root.insertAdjacentHTML("afterbegin", makeHeaderBlock(ctx.headerBg));
    editorDom.initSortable();
  }, [editorDom, ctx.headerBg]);

  const insertFooterBlock = useCallback(() => {
    editorDom.ensureRoot();
    const root = editorDom.getRoot();
    if (!root) return;
    if (root.querySelector('[data-bc-section="footer"]')) {
      alert("Only one footer allowed.");
      return;
    }
    root.insertAdjacentHTML("beforeend", makeFooterBlock());
    editorDom.initSortable();
  }, [editorDom]);

  const handleButtonApply = useCallback(({ text, url }) => {
    const active = window.__bcActiveButton;
    if (!active) return;

    const normalized = imageUpload.normalizeUrl(url);
    active.textContent = text || "Button Text";
    active.setAttribute("data-btn-label", text || "Button Text");
    active.setAttribute("data-btn-url", normalized);
    active.href = normalized || "#";
    // Also update book card buy button attrs if present
    if (active.hasAttribute("data-buy-url")) {
      active.setAttribute("data-buy-url", normalized);
      active.setAttribute("data-buy-label", text || "Buy Now");
    }
    if (normalized) {
      active.removeAttribute("data-placeholder-link");
    } else {
      active.setAttribute("data-placeholder-link", "");
    }
  }, [imageUpload]);

  const handleVideoSave = useCallback(({ url }) => {
    if (!url?.trim()) return;
    editorDom.insertBlockSmart(makeVideoEmbedBlock(url.trim()));
  }, [editorDom]);

  const handleSocialIcons = useCallback((links) => {
    if (socialModalPlacement === "footer") {
      ctx.setFooter((prev) => ({ ...prev, socialLinks: links }));
      return;
    }
    editorDom.insertBlockSmart(makeSocialIconsBlock(links));
  }, [editorDom, socialModalPlacement, ctx]);

  const applyPreset = useCallback((key) => {
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    ctx.setPresetKey(p.key);
    ctx.setOuterBg(p.outerBg);
    ctx.setHeaderBg(p.accentColor);
    ctx.setHeaderTextColor(p.headerTextColor || "#ffffff");
    ctx.setHeaderButtonColor(p.headerButtonColor || "#ffffff");
    ctx.setHeaderButtonText(p.headerButtonText || "Learn More");
    ctx.setCardBg(p.cardBg);
    ctx.setTextColor(p.textColor);
    ctx.setAccentColor(p.accentColor);
    ctx.setFontFamily(p.fontFamily);
    ctx.setFontSize(p.fontSize || "16px");
    ctx.setContentWidth(p.contentWidth);
  }, [ctx]);

  const onEditorKeyDown = useCallback((e) => {
    // Undo/Redo
    if ((e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault();
      if (e.shiftKey) {
        undoRedo.redo();
      } else {
        undoRedo.undo();
      }
      return;
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      const sel = window.getSelection();
      const root = editorDom.getRoot();
      if (!sel || !root || !sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      if (range.commonAncestorContainer === ctx.editorRef.current) {
        e.preventDefault();
        root.innerHTML = "";
        editorDom.insertBlockSmart(makeTextBlock(""));
      }
    }

    // Snapshot for undo history + trigger auto-save
    isDirtyRef.current = true;
    triggerSave();
  }, [editorDom, ctx.editorRef, undoRedo, triggerSave]);

  /* ---- SAVE STATUS ---- */
  const saveStatus = ctx.saving ? "saving" : autoSaveStatus;

  /* ---- BLOCK GROUPS (Hayley: removed Quote & Highlight) ---- */
  const blockGroups = [
    {
      title: "Branding",
      items: [
        { label: "Header", onClick: insertHeaderBlock, icon: PanelTop },
        { label: "Footer", onClick: insertFooterBlock, icon: PanelTop },
      ],
    },
    {
      title: "Structure",
      items: [
        { label: "Banner Header", onClick: () => editorDom.insertBlockSmart(makeBannerBlock(ctx.accentColor)), icon: LayoutTemplate },
        { label: "Section", onClick: () => editorDom.insertBlockSmart(makeSectionBlock(ctx.cardBg)), icon: Layout },
        { label: "2 Columns", onClick: () => editorDom.insertBlockSmart(makeTwoColBlock()), icon: Columns },
        { label: "Image + Text", onClick: () => editorDom.insertBlockSmart(makeImageTextBlock()), icon: Image },
      ],
    },
    {
      title: "Content",
      items: [
        { label: "Text", onClick: () => editorDom.insertBlockSmart(makeTextBlock()), icon: Type },
        { label: "Button", onClick: () => editorDom.insertBlockSmart(makeButtonBlock("Button Text", "", ctx.accentColor)), icon: MousePointerClick },
        { label: "Book Card", onClick: () => editorDom.insertBlockSmart(makeBookCardBlock(ctx.accentColor)), icon: BookOpen },
      ],
    },
    {
      title: "Media & Spacing",
      items: [
        { label: "Image Gallery", onClick: () => editorDom.insertBlockSmart(makeImageGalleryBlock()), icon: ImagePlus },
        { label: "Banner Image", onClick: () => editorDom.insertBlockSmart(makeBannerImageBlock()), icon: Image },
        { label: "Video Embed", onClick: () => setVideoModalOpen(true), icon: Video },
        { label: "Divider", onClick: () => editorDom.insertBlockSmart(makeDividerBlock()), icon: Minus },
        { label: "Spacer", onClick: () => editorDom.insertBlockSmart(makeSpacerBlock()), icon: Space },
      ],
    },
    {
      title: "Social",
      items: [
        {
          label: "Social Icons Block",
          onClick: () => {
            setSocialModalPlacement("body");
            setSocialModalOpen(true);
          },
          icon: Share2,
        },
      ],
    },
  ];

  const getPreviewHtml = useCallback(() => {
    const snapshot = draftPersistence.capture();
    return buildEmailHtml({ ...snapshot.design, bodyHtml: snapshot.body, subject: snapshot.subject,
      preheaderText: snapshot.preheaderText, footer: snapshot.footer, blockStyles: snapshot.blockStyles });
  }, [draftPersistence]);
  const handleNewNewsletter = useCallback(() => {
    draftPersistence.clearDraft();
  }, [draftPersistence]);

  return (
    <div
      className="min-h-screen w-full bg-bookish-cream"
      style={{ "--composer-header-offset": headerOffset }}
    >
      {/* ---- TOP BAR ---- */}
      <header className="w-full bg-bookish-royal text-bookish-cream shadow-md md:sticky md:top-0 z-50">
        <div className="w-full px-4 md:px-6 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* LEFT: Nav + New + Title + Subject */}
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:flex-1 min-w-0">
              <button
                onClick={() => { if (window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) { isDirtyRef.current = false; window.location.href = runtime.mailCenterUrl; } }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-bookish-cream rounded-lg border border-white/20 hover:bg-white/25 transition-colors whitespace-nowrap"
              >
                <ChevronLeft size={14} />
                Return to Mail Center
              </button>
              <a
                href={runtime.portalUrl}
                onClick={event => { if (!window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) event.preventDefault(); else isDirtyRef.current = false; }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-bookish-cream rounded-lg border border-white/20 hover:bg-white/25 transition-colors whitespace-nowrap"
              >
                <ChevronLeft size={14} />
                Return to Portal
              </a>
              {/*<h1 className="text-lg font-serif text-bookish-cream whitespace-nowrap hidden md:block">
                {ctx.composerMode === "review"
                  ? "Edit Newsletter"
                  : ctx.newsletterId ? "Edit Newsletter" : "Newsletter Editor"}
              </h1>*/}
              {ctx.composerMode === "design" && (
                <input
                  type="text"
                  value={ctx.subject}
                  onChange={(e) => ctx.setSubject(e.target.value)}
                  placeholder="Subject line..."
                  aria-label="Newsletter subject"
                  maxLength={500}
                  className="w-full sm:w-auto sm:flex-1 min-w-0 min-h-[44px] sm:max-w-md rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm px-3 py-1.5 text-sm text-bookish-cream placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
                />
              )}
              <SaveIndicator status={saveStatus} />
            </div>

            {/* RIGHT: Preview toggle + actions */}
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              {ctx.composerMode === "design" && (
                <PreviewToggle mode={ctx.previewMode} onChange={ctx.setPreviewMode} />
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={newsletterApi.saveDraftServer}
                  disabled={ctx.saving || ctx.loading || ctx.uploading || !ctx.ready}
                  className="px-3 py-2 text-sm font-medium bg-white/15 text-bookish-cream rounded-lg border border-white/20 hover:bg-white/25 transition-colors disabled:opacity-50"
                >
                  {ctx.saving ? "Saving..." : "Save"}
                </button>
                <button className="px-3 py-2 text-sm border border-white/20 rounded-lg disabled:opacity-50" disabled={!ctx.ready || ctx.saving || ctx.loading || ctx.uploading} onClick={() => newsletterApi.saveDraftServer({ asNew: true })}>Save a new copy</button>
                {ctx.composerMode === "design" ? (
                  <button
                    disabled={!ctx.ready || ctx.saving || ctx.loading || ctx.uploading}
                    onClick={() => ctx.setComposerMode("review")}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white text-bookish-purple rounded-lg hover:bg-bookish-cream transition-colors"
                  >
                    {runtime.canSend === false ? "Review newsletter" : "Review & Send"}
                    <ArrowRight size={15} />
                  </button>
                ) : (
                  <button
                    onClick={() => ctx.setComposerMode("design")}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white text-bookish-purple rounded-lg hover:bg-bookish-cream transition-colors"
                  >
                    <ArrowLeft size={15} />
                    Back to Editor
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {ctx.notice && <p role="status" className="m-3 rounded border bg-white p-3 text-bookish-purple">{ctx.notice}</p>}
      {ctx.loadError && <div role="alert" className="m-3 rounded border bg-red-50 p-3"><p>{ctx.loadError}</p><button className="p-3 underline" onClick={() => { const id = new URLSearchParams(window.location.search).get('newsletterId'); if (id) newsletterApi.loadNewsletterFromApi(id); else { try { const restored = draftPersistence.restoreDraft(); ctx.setLoadError(''); if (!restored && runtime.fixedTemplate) templates.loadInitialTemplate(); } catch (e) { ctx.setLoadError(e.message); } } }}>Retry opening draft</button><button className="p-3 underline" onClick={() => { if (window.confirm('Start a separate new draft? The previous recovery copy will be left untouched.')) { ctx.setNewsletterId(crypto.randomUUID()); ctx.setVersion(null); editorDom.setEditorHtml(''); ctx.setReady(true); ctx.setLoadError(''); } }}>Start a separate draft</button></div>}
      <div className="mx-3 flex flex-wrap gap-3 text-sm"><span>Autosave keeps a recovery copy in this browser. Save keeps your draft in your account.</span><button className="underline min-h-[44px]" disabled={ctx.saving || ctx.loading} onClick={templates.loadTemplatesFromApi}>Reload templates</button></div>
      <fieldset inert={!ctx.ready || ctx.loading || ctx.uploading ? "" : undefined} disabled={!ctx.ready || ctx.loading || ctx.uploading} className="min-w-0 border-0 p-0 m-0">
      {/* ---- MOBILE SIDEBAR TOGGLES ---- */}
      {ctx.composerMode === "design" && (
        <div className="flex md:hidden gap-2 px-3 pt-2">
          <button
            aria-expanded={leftSidebarOpen}
            onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-bookish-purple/15 bg-white hover:bg-bookish-purple/5 transition-colors shadow-sm"
          >
            <PanelLeftClose size={16} />
            Blocks
          </button>
          <button
            aria-expanded={rightSidebarOpen}
            onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-bookish-purple/15 bg-white hover:bg-bookish-purple/5 transition-colors shadow-sm"
          >
            <PanelRightClose size={16} />
            Settings
          </button>
        </div>
      )}

      {/* ---- MOBILE SIDEBAR OVERLAYS ---- */}
      {ctx.composerMode === "design" && (
        <>
          {leftSidebarOpen && (
            <>

              <div className="md:hidden bg-white border m-3 rounded p-3"><button className="p-3 underline" onClick={() => setLeftSidebarOpen(false)}>Close blocks</button>
                <BlocksSidebar blockGroups={blockGroups} />
              </div>
            </>
          )}

          {rightSidebarOpen && (
            <>

              <div className="md:hidden bg-white border m-3 rounded p-3"><button className="p-3 underline" onClick={() => setRightSidebarOpen(false)}>Close settings</button>
                <DesignSidebar
                  applyPreset={applyPreset}
                  addHeaderLink={handleAddHeaderLink}
                  editFooterSocialLinks={() => {
                    setSocialModalPlacement("footer");
                    setSocialModalOpen(true);
                  }}
                  saveCurrentAsTemplate={templates.saveCurrentAsTemplate}
                  applyTemplate={templates.applyTemplate}
                  deleteTemplate={templates.deleteTemplate}
                reloadTemplate={() => { if (window.confirm("Start a new letter from the saved template? Save your current draft first to keep it.")) templates.loadInitialTemplate({ preserveOnFailure: true }); }}
                />
              </div>
            </>
          )}
        </>
      )}


      {/* ---- BUILDER (hidden via CSS in review mode to preserve DOM) ---- */}
      <div className={`${ctx.composerMode === "design" ? "" : "hidden"}`}>
        {/* EDITOR PLACEHOLDER STYLES */}
        <style>
          {`
            .newsletter-editor b, .newsletter-editor strong { font-weight: bold; }
            .newsletter-editor i, .newsletter-editor em { font-style: italic; }
            .newsletter-editor u { text-decoration: underline; }
            .newsletter-editor h2 { font-size:22px; font-weight:bold; }
            .newsletter-editor h3 { font-size:18px; font-weight:bold; }
            .newsletter-editor ul { list-style:disc; padding-left:22px; }
            .newsletter-editor ol { list-style:decimal; padding-left:22px; }
            [data-placeholder][data-empty="true"]:not(:focus)::before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
            [data-placeholder]:not(:focus):empty::before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
            [data-placeholder]:focus::before {
              content: "";
            }
            [data-bc-section] {
              transition: border-color .15s ease;
              border-radius: 12px;
              border: 2px solid transparent;
              margin: 10px 0 16px 0;
              padding-top: 2px;
              z-index: 1;
            }
            [data-bc-section]:hover {
              border-color: rgba(63,37,107,0.15);
            }
            [data-bc-drag], [data-bc-advanced] {
              opacity: 0;
              transition: opacity .15s ease;
              z-index: 30 !important;
            }
            [data-bc-section]:hover [data-bc-drag],
            [data-bc-section]:hover [data-bc-advanced] {
              opacity: 0.9;
            }
            [data-bc-drag]:hover {
              opacity: 1 !important;
              background: rgba(63,37,107,0.08) !important;
            }
            [data-bc-section]:hover [data-bc-label] {
              opacity: 1 !important;
            }
          `}
        </style>

        <div className="mx-auto flex w-full max-w-[1680px] overflow-hidden border border-bookish-purple/12 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.12)] ring-1 ring-white/70">
          <div className="hidden md:block w-72 shrink-0 border-r border-bookish-purple/10 bg-gradient-to-b from-white to-bookish-cream/40">
            <div className="sticky top-[var(--composer-header-offset)] max-h-[calc(100vh-var(--composer-header-offset))] overflow-y-auto p-4">
              <BlocksSidebar
                blockGroups={blockGroups}
                compact
                className="w-full"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
          <BrandingBanner uploadImage={imageUpload.uploadImage} ensureRoot={editorDom.ensureRoot} getRoot={editorDom.getRoot} />
          <EditorCanvas
            exec={editorDom.exec}
            insertLink={editorDom.insertLink}
            clearFormatting={editorDom.clearFormatting}
            normalizeEditor={editorDom.normalizeEditor}
            onKeyDown={onEditorKeyDown}
            onUndo={undoRedo.undo}
            onRedo={undoRedo.redo}
            onAddTextBlock={() => editorDom.insertBlockSmart(makeTextBlock())}
            onBrowseTemplates={() => setRightSidebarOpen(true)}
            onClearCanvas={() => setNewConfirmOpen(true)}
            onInsertInlineSocialSnippet={ENABLE_INLINE_SOCIAL_SNIPPET_INSERTION ? () => {
              setSocialModalPlacement("body");
              setSocialModalOpen(true);
            } : null}
          />

          </div>
          <div className="hidden md:block w-80 shrink-0 border-l border-bookish-purple/10 bg-gradient-to-b from-white to-bookish-cream/40">
            <div className="sticky top-[var(--composer-header-offset)] max-h-[calc(100vh-var(--composer-header-offset))] overflow-y-auto p-4">
              <DesignSidebar
                applyPreset={applyPreset}
                addHeaderLink={handleAddHeaderLink}
                editFooterSocialLinks={() => {
                  setSocialModalPlacement("footer");
                  setSocialModalOpen(true);
                }}
                saveCurrentAsTemplate={templates.saveCurrentAsTemplate}
                applyTemplate={templates.applyTemplate}
                deleteTemplate={templates.deleteTemplate}
                reloadTemplate={() => { if (window.confirm("Start a new letter from the saved template? Save your current draft first to keep it.")) templates.loadInitialTemplate({ preserveOnFailure: true }); }}
                compact
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---- REVIEW & SEND MODE ---- */}
      {ctx.composerMode === "review" && (
        <div className="px-3 pt-3 md:px-4">
          <ReviewScreen
            capture={draftPersistence.capture}
            saveDraftLocal={draftPersistence.saveDraftLocal}
            getPreviewHtml={getPreviewHtml}
            saveDraftServer={newsletterApi.saveDraftServer}
            sendNewsletter={newsletterApi.sendNewsletter}
            pendingDelivery={newsletterApi.pendingDelivery}
            deliveryResult={newsletterApi.deliveryResult}
          />
        </div>
      )}

      <InlineBlockActions
        moveSection={editorDom.moveSection}
        duplicateSection={editorDom.duplicateSection}
        deleteSection={editorDom.deleteSection}
        onOpenAdvancedSettings={(blockId) => blockStylesCtx.openAdvancedBlock(blockId)}
      />


      {blockStylesCtx.activeAdvancedBlockId && blockSettingsPos && (
        <div
          ref={blockSettingsRef}
          data-bc-popover
          className="fixed z-[95] w-[300px] max-h-[70vh] overflow-y-auto rounded-2xl border border-bookish-purple/12 bg-white p-3 shadow-2xl"
          style={{ top: blockSettingsPos.top - window.scrollY, left: blockSettingsPos.left - window.scrollX }}
        >
          <BlockSettingsPanel
            blockId={blockStylesCtx.activeAdvancedBlockId}
            onClose={blockStylesCtx.closeAdvancedBlock}
          />
        </div>
      )}

      <AutoSaveToast saveStatus={saveStatus} />

      <ImagePopover
        applyImageLink={imageUpload.applyImageLink}
        addImageToActiveRow={imageUpload.addImageToActiveRow}
        onReplaceActiveImage={imageUpload.onReplaceActiveImage}
        onUploadToSlot={imageUpload.onUploadToSlot}
        onRemoveActiveImage={imageUpload.onRemoveActiveImage}
        setThumbnailRowSize={imageUpload.setThumbnailRowSize}
      />

      <ButtonSettingsPopover onApply={handleButtonApply} />

      {/* FILE INPUT */}
      <input
        ref={ctx.fileInputRef}
        type="file"
        accept={runtime.upload ? "image/jpeg,image/png,image/webp" : "image/jpeg,image/png,image/webp,image/gif"}
        hidden
        onChange={imageUpload.onFileChange}
      />

      {/* SOCIAL LINKS MODAL */}
      <SocialLinksModal
        isOpen={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        onSave={handleSocialIcons}
        initialLinks={socialModalPlacement === "footer" ? ctx.footer?.socialLinks || {} : {}}
        title={socialModalPlacement === "footer" ? "Footer Social Media Links" : "Social Media Links"}
        submitLabel={socialModalPlacement === "footer" ? "Save Footer Icons" : "Insert Social Icons Block"}
      />

      {/* HEADER LINK MODAL */}
      <InputModal
        isOpen={headerLinkModalOpen}
        onClose={() => setHeaderLinkModalOpen(false)}
        icon={PanelTop}
        title="Add Header Link"
        fields={[
          { key: "label", label: "Link Text", placeholder: "e.g. Home", required: true },
          { key: "url", label: "Link URL", placeholder: "https://example.com", required: true },
        ]}
        submitLabel="Add Link"
        onSubmit={handleHeaderLinkSave}
      />

      {/* VIDEO EMBED MODAL */}
      <InputModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        icon={Video}
        title="Embed Video"
        fields={[
          { key: "url", label: "YouTube or Vimeo URL", placeholder: "https://youtube.com/watch?v=...", required: true, hint: "Paste the full URL from your browser" },
        ]}
        submitLabel="Embed Video"
        onSubmit={handleVideoSave}
      />

      {/* NEW NEWSLETTER CONFIRM MODAL */}
      <NewConfirmModal
        isOpen={newConfirmOpen}
        onClose={() => setNewConfirmOpen(false)}
        onConfirm={handleNewNewsletter}
      />
      </fieldset>
    </div>
  );
}

export default function NewsletterComposer() {
  const runtime = useMailRuntime();
  const [owner, setOwner] = useState(null), [error, setError] = useState(''), [attempt, setAttempt] = useState(0);
  useEffect(() => { let active = true; setError(''); mailIdentity(runtime).then(identity => { if (active) setOwner(identity.attributes.sub); }).catch(e => { if (active) setError(e.message); }); return () => { active = false; }; }, [runtime, attempt]);
  if (!owner) return <div className="p-6 bg-bookish-cream">{error ? <><p role="alert">{error}</p><button onClick={() => setAttempt(a => a + 1)}>Retry composer</button></> : <p role="status">Opening your newsletter workspace…</p>}</div>;
  return (
    <ComposerProvider owner={owner} key={owner}>
      <BlockStylesProvider>
        <ComposerInner />
      </BlockStylesProvider>
    </ComposerProvider>
  );
}
